<template>
  <Transition name="modal">
    <div v-if="show" class="modal-mask" v-bind="$attrs" @click.self="requestClose">
      <div class="modal-container" role="dialog" aria-modal="true">
        <div class="modal-header">
          <slot name="header"></slot>
          <button
              type="button"
              class="btn-close"
              :disabled="closeDisabled"
              :aria-label="closeLabel"
              @click="requestClose"
          >&times;</button>
        </div>

        <div class="modal-body">
          <slot name="body"></slot>
        </div>

        <!-- 버튼 줄은 넘겨받았을 때만 — 빈 띠를 남기지 않는다 (SURINP-693) -->
        <div v-if="$slots.footer" class="modal-footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </Transition>
</template>
<script>
import i18n from '@/locales/i18n'

/**
 * @component RdModal
 * @description 모든 모달이 같이 쓰는 단일 틀(SURINP-693, design-review ④-6).
 * 제목(header 슬롯) · × 닫기 · 본문 · 분리된 버튼 줄(footer 슬롯, 있을 때만)을 한 틀에 담는다.
 * 배경은 사이드바까지 화면 전체를 어둡게 하고, PC(1280+)에서는 사이드바 폭만큼 비켜 내용 영역 가운데에 띄운다.
 * Teleport 를 쓰지 않는다 — 사용처 scoped `:deep(.modal-container) { width }` 너비 지정이 그대로 닿게 하기 위해서다.
 * @slot header - 제목 (h3 등. 전역 .modal-title 의 꺾쇠 배경은 이 틀 안에서 지운다)
 * @slot body - 본문
 * @slot footer - 버튼 줄
 */
export default {
  name: 'RdModal',
  // 루트가 Transition 이라 사용처 class(너비 지정 기준점)를 배경(.modal-mask)에 직접 붙인다
  inheritAttrs: false,
  // ── Props ──
  props: {
    /**
     * 모달 표시 여부 (v-model:show)
     * @type {boolean}
     */
    show: { type: Boolean, default: false },
    /**
     * true 면 × 와 바깥 클릭으로 닫히지 않는다 (요청 중 잠금)
     * @type {boolean}
     */
    closeDisabled: { type: Boolean, default: false }
  },
  emits: ['update:show', 'close'],
  // ── 계산된 속성 ──
  computed: {
    /**
     * × 의 읽기용 이름 — 틀은 모든 화면·테스트가 같이 쓰므로 전역 $t 설치 여부에 기대지 않는다
     */
    closeLabel() {
      return i18n.global.t('button.close')
    }
  },
  // ── 메서드 ──
  methods: {
    /**
     * × 또는 바깥 클릭 — 잠겨 있지 않으면 닫힘을 알린다.
     */
    requestClose() {
      if (this.closeDisabled) return
      this.$emit('update:show', false)
      this.$emit('close')
    }
  }
}
</script>
<style scoped>
/* === 기본 모달 스타일 (모든 해상도) === */
.modal-mask {
  position: fixed;
  z-index: var(--z-modal, 300);
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
}
.modal-container {
  width: 90%;
  max-width: 640px;
  max-height: 90vh;
  margin: auto;
  padding: 0;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e8eaed;
  flex-shrink: 0;
}
/* 전역 typography.css .modal-title 의 「>」 꺾쇠 배경·왼쪽 여백을 틀 안에서만 지운다 */
.modal-header :slotted(.modal-title) {
  background: none;
  padding-left: 0;
}
.modal-header :slotted(h1),
.modal-header :slotted(h3) {
  margin: 0;
  font-size: var(--font-heading-md, 1.0625rem) !important;
  font-weight: var(--font-weight-bold, 700);
  color: var(--color-gray-800, #1E2228);
}
/* 닫기 — 상자 없는 × 아이콘 */
.modal-header .btn-close {
  flex-shrink: 0;
  margin-left: auto;
  padding: 4px 8px;
  border: 0;
  background: transparent;
  box-shadow: none;
  font-size: 24px;
  line-height: 1;
  color: var(--color-gray-500, #5C616B);
  cursor: pointer;
}
.modal-header .btn-close:hover {
  color: var(--color-gray-800, #1E2228);
}
.modal-header .btn-close:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #e8eaed;
  flex-shrink: 0;
}
.modal-footer:empty {
  display: none;
}
.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95);
  opacity: 0;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* === PC 리디자인 (1280px+) === */
@media (min-width: 1280px) {
  /* --- 배경은 사이드바까지 덮고, 모달은 사이드바 폭만큼 비켜 내용 영역 가운데 --- */
  .modal-mask {
    left: 0;
    padding-left: var(--sidebar-width, 260px);
    background-color: var(--color-overlay, rgba(0, 0, 0, 0.5));
    backdrop-filter: blur(1.5px);
    transition: opacity var(--transition-slow, 0.3s ease);
  }

  /* --- 모달 컨테이너 (640px 기본 너비) — 사용처에서 :deep(.modal-container) 로 너비 덮어쓰기 --- */
  .modal-container {
    width: 640px;
    background-color: var(--color-white, #FFFFFF);
    border-radius: var(--radius-xl, 16px);
    box-shadow: var(--shadow-modal, 0 20px 60px rgba(0, 0, 0, 0.2));
    transition: all var(--transition-slow, 0.3s ease);
  }

  .modal-header {
    padding: var(--space-5, 20px) var(--space-6, 24px);
    border-bottom: 1px solid var(--color-gray-100, #E8EAED);
  }

  .modal-body {
    padding: var(--space-6, 24px);
    margin: 0;
  }

  /* --- 버튼 줄: 오른쪽 정렬 --- */
  .modal-footer {
    gap: var(--space-2, 8px);
    padding: var(--space-4, 16px) var(--space-6, 24px);
    border-top: 1px solid var(--color-gray-100, #E8EAED);
  }

  .modal-footer :slotted(button) {
    padding: 8px 20px !important;
    border-radius: var(--radius-md, 8px) !important;
    font-size: var(--font-body-md, 0.875rem) !important;
    font-weight: var(--font-weight-medium, 500);
    cursor: pointer;
    line-height: 24px !important;
    min-width: 80px;
    text-align: center;
    transition: all var(--transition-fast, 0.15s ease);
  }
}
</style>
